import { _, helpers, mui, React, ts, ui, useSelector } from '_core';

import { getDataTabs, transform } from './helpers';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  file: string;
  fullScreen: boolean;
  scale: ts.enums.SCALE_ENUM;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  title: string;
};

const Chart = ({ id, readOnly, file, fullScreen, loadWidgetData, title }: ChartProps) => {
  const tabs = [
    'industry',
    'active_industry',
    'country',
    'active_country',
    'riskfactor',
    'active_riskfactor',
    'risk',
    'active_risk',
    'signal',
    'active_signal',
    'tcost_constraint',
    'turnover_constraint',
    'gross_leverage_constraint',
    'net_leverage_constraint',
  ];

  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const [displayTabs, setDisplayTabs] = React.useState<string[]>();
  const [selectedTab, setSelectedTab] = React.useState<string>();

  const resources = useSelector((state) => state.resources);

  // Let's use the name count endpoint as it will get the data and
  // prepare it for a linear chart
  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
          rename: { build_date: 'date' },
        },
      });
      if (response) {
        const newTabs = getDataTabs(response.data[0], tabs);

        setSelectedTab(newTabs[0]);
        setDisplayTabs(newTabs);
        setData(response.data);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData();
    return () => setData(null);
  }, [file]);

  const formattedData = React.useMemo(() => {
    if (!data) return null;
    return transform(data, tabs, resources)[selectedTab] || [];
  }, [data, selectedTab]);

  const getLabel = (tab: string) => {
    if (tab == 'riskfactor') return 'Risk Factor';
    if (tab == 'active_riskfactor') return 'Active Risk Factor';
    if (tab == 'tcost_constraint') return 'Max TCost';
    if (tab == 'turnover_constraint') return 'Max Turnover';
    if (tab == 'risk') return 'Max Risk';
    if (tab == 'gross_leverage_constraint') return 'Gross';
    if (tab == 'net_leverage_constraint') return 'Net';

    return _.startCase(tab);
  };

  const getYAxisLabel = (tab: string) => {
    if (tab == 'riskfactor') return 'Risk Factor Exposure';
    if (tab == 'active_riskfactor') return 'Active Risk Factor Exposure';
    if (tab == 'tcost_constraint') return 'Transaction cost (bps)';
    if (tab == 'turnover_constraint') return 'Turnover';
    if (tab == 'risk') return 'Risk %';
    if (tab == 'gross_leverage_constraint') return 'Gross Leverage';
    if (tab == 'net_leverage_constraint') return 'Net Leverage';

    return _.startCase(tab) + ' Exposure';
  };

  const getTabs = () => {
    if (displayTabs) {
      return (
        <mui.core.Tabs
          indicatorColor="primary"
          textColor="primary"
          value={selectedTab}
          onChange={(e, newTab) => setSelectedTab(newTab)}
          aria-label="Tabs"
        >
          {displayTabs.map((tab) => (
            <mui.core.Tab key={tab} label={getLabel(tab)} value={tab} />
          ))}
        </mui.core.Tabs>
      );
    }
    return null;
  };

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }}>
      {getTabs()}
      <mui.core.Box p={3} flex="1">
        <ui.LineChart
          id={id}
          data={formattedData}
          fullScreen={fullScreen}
          readOnly={readOnly}
          yAxisLabel={getYAxisLabel(selectedTab)}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          exportTitle={title}
        />
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
